import {Button, Toolbar, ToolbarItem} from "@progress/kendo-react-buttons"
import {
    Field,
    FieldRenderProps,
    Form,
    FormClassComponent,
    FormRenderProps,
    FormSubmitClickEvent,
} from "@progress/kendo-react-form"
import {Input, Switch} from "@progress/kendo-react-inputs"
import {Hint} from "@progress/kendo-react-labels"
import {
    MDBBtn,
    MDBCol,
    MDBModal,
    MDBModalBody,
    MDBModalContent,
    MDBModalDialog,
    MDBModalHeader,
    MDBModalTitle,
    MDBRow,
} from "mdbreact"
import {createRef, useCallback, useEffect, useState} from "react"
import {setToastr} from "../../containers/App/appSlice"
import {
    addDealer,
    Dealer,
    initDealer,
    setModalToggle,
    setSelectedDealer,
    updateDealerStatus,
    updatePreparerId,
} from "../../containers/RegionAndDealers/RADSlice"
import {RootState} from "../../rootReducer"
import {SpinnerFragment} from "../../shared/CommonItems"
import {useAppDispatch, useAppSelector} from "../../store"
import {PermissionsChecker} from "../Shared/permissionChecker"

const AddEditDealerModal = () => {
    const dispatch = useAppDispatch()
    const isModalToggled = useAppSelector((state: RootState) => state.containers.regions.modalToggle)
    const data = useAppSelector((state: RootState) => state.containers.regions.selectedDealer)
    const testStationsById = useAppSelector((state: RootState) => state.containers.testStations.testStationsData)
    const isLoading = useAppSelector((state: RootState) => state.containers.regions.isLoading)
    const regionSelected = useAppSelector((state: RootState) => state.containers.regions.regionSelected)
    const testStAssigned = testStationsById.filter(t => t.dealerID == data.dealerId).some(t => t.dealerDefault)

    const [selectedDealer, setDealerData] = useState<Dealer>(data)

    const formEl = createRef<FormClassComponent>()

    useEffect(() => {
        setDealerData(data)
    }, [data])

    useEffect(() => {
        isModalToggled && formEl.current?.resetForm()
    }, [isModalToggled])

    const errorToastr = (message: string) => {
        dispatch(
            setToastr({
                isActive: true,
                message: message,
                delay: 3000,
                title: "Error",
                type: "danger",
                description: "",
            }),
        )
    }
    const handleSubmit = useCallback(
        (e: FormSubmitClickEvent) => {
            const updateDealer = e.values
            if (e.values.dealerId > 0 && e.isValid && data !== null) {
                if (updateDealer.dealerName.trim().length === 0) errorToastr("Enter dealer name")
                else {
                    dispatch(
                        updatePreparerId({
                            ...updateDealer,
                            dealerNumber: updateDealer.dealerNumber.trim(),
                            preparerId: updateDealer.preparerId.trim(),
                            phoneNumber: updateDealer.phoneNumber != null ? updateDealer.phoneNumber.trim() : "",
                        } as Dealer),
                    )
                }
            } else {
                const newDealer = e.values
                if (newDealer.dealerName.trim().length === 0) errorToastr("Enter dealer name")
                else
                    dispatch(
                        addDealer(regionSelected.regionId, {
                            ...newDealer,
                            dealerNumber: newDealer.dealerNumber.trim(),
                            preparerId: newDealer.preparerId.trim(),
                            phoneNumber: newDealer.phoneNumber != null ? newDealer.phoneNumber.trim() : "",
                            regionId: regionSelected.regionId,
                        }),
                    )
            }
        },
        [dispatch, data, regionSelected],
    )

    const IsATFSwitch = (props: FieldRenderProps) => {
        const {name, value, onChange} = props

        return (
            <Switch
                name={name}
                checked={value}
                onChange={onChange}
                disabled={testStAssigned || !selectedDealer.active}
            />
        )
    }

    const activeHandler = (e: any) => {
        e.preventDefault()
        dispatch(
            updateDealerStatus(
                formEl.current?.values.regionId,
                formEl.current?.values.dealerId,
                formEl.current?.values.active,
            ),
        )
    }
    return (
        <div>
            {
                <MDBModal staticBackdrop animationDirection="right" show={isModalToggled} tabIndex="-1">
                    <MDBModalDialog position="top right modal-m" side>
                        <MDBModalContent className="full-height">
                            <MDBModalHeader>
                                <MDBModalTitle>
                                    {data.dealerId > 0
                                        ? data.dealerName
                                        : `New Dealer for ${regionSelected.regionName}`}
                                </MDBModalTitle>
                                <MDBBtn
                                    color="none"
                                    className="btn-close"
                                    onClick={() => {
                                        dispatch(setModalToggle(false))
                                        dispatch(setSelectedDealer(initDealer()))
                                        formEl.current?.resetForm()
                                    }}
                                ></MDBBtn>
                            </MDBModalHeader>
                            {isLoading && <SpinnerFragment />}
                            <PermissionsChecker permission={["Dealers.Edit"]}>
                                <Toolbar className="shadow-0 border-bottom p-0 pb-1 pt-1">
                                    <ToolbarItem>
                                        <Button
                                            fillMode="flat"
                                            themeColor={"primary"}
                                            iconClass="fa fa-save"
                                            onClick={e => {
                                                formEl.current && formEl.current.onSubmit(e)
                                            }}
                                            title="Save"
                                            disabled={!data.active}
                                        >
                                            Save
                                        </Button>

                                        {data.dealerId > 0 && (
                                            <Button
                                                className="ms-2"
                                                fillMode="flat"
                                                themeColor={"primary"}
                                                icon={`k-icon k-i-${data.active ? "cancel" : "reset"}`}
                                                tabIndex={-1}
                                                onClick={activeHandler}
                                            >
                                                {data.active ? `Deactivate` : `Activate`}
                                            </Button>
                                        )}
                                    </ToolbarItem>
                                </Toolbar>
                            </PermissionsChecker>

                            <MDBModalBody>
                                <fieldset disabled={!selectedDealer.active}>
                                    <Form
                                        ref={formEl}
                                        key={JSON.stringify(selectedDealer)}
                                        initialValues={selectedDealer}
                                        onSubmitClick={handleSubmit}
                                        render={(formRenderProps: FormRenderProps) => (
                                            <MDBRow>
                                                <MDBCol>
                                                    {data.dealerId >= 0 && (
                                                        <MDBRow className="mb-3">
                                                            <MDBCol>
                                                                <Field
                                                                    name="dealerName"
                                                                    id="dealerName"
                                                                    component={Input}
                                                                    required
                                                                    label="Dealer Name"
                                                                />
                                                            </MDBCol>
                                                        </MDBRow>
                                                    )}
                                                    <MDBRow className="mb-3">
                                                        <MDBCol>
                                                            <Field
                                                                name="dealerNumber"
                                                                id="dealerNumber"
                                                                component={Input}
                                                                label="Dealer Number"
                                                            />
                                                        </MDBCol>
                                                    </MDBRow>
                                                    <MDBRow className="mb-3">
                                                        <MDBCol>
                                                            <Field
                                                                name="preparerId"
                                                                id="preparerId"
                                                                component={Input}
                                                                label="Preparer Id"
                                                            />
                                                        </MDBCol>
                                                    </MDBRow>
                                                    <MDBRow className="mb-3">
                                                        <MDBCol>
                                                            <Field
                                                                name="phoneNumber"
                                                                id="phoneNumber"
                                                                component={Input}
                                                                label="Phone Number"
                                                            />
                                                        </MDBCol>
                                                    </MDBRow>
                                                    <MDBRow className="mb-3">
                                                        <MDBCol>Has Local ATF</MDBCol>
                                                    </MDBRow>
                                                    <MDBRow className="mb-3">
                                                        <MDBCol>
                                                            <Field
                                                                name="hasLocalATF"
                                                                component={IsATFSwitch}
                                                                label="Has Local ATF "
                                                                value={selectedDealer.hasLocalATF}
                                                            />
                                                            {testStAssigned && (
                                                                <Hint className="text-muted mt-2">
                                                                    Test station already associated with dealer, cannot
                                                                    deactivate
                                                                </Hint>
                                                            )}
                                                        </MDBCol>
                                                    </MDBRow>
                                                </MDBCol>
                                            </MDBRow>
                                        )}
                                    />
                                </fieldset>
                            </MDBModalBody>
                        </MDBModalContent>
                    </MDBModalDialog>
                </MDBModal>
            }
        </div>
    )
}

export default AddEditDealerModal
